import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { Banner, Loading, StatusPill } from '../components/ui';
import { formatDateTime } from '../lib/format';
import { SignExperience } from './Sign';
import { EmbeddedPrepare } from './EmbeddedPrepare';

/**
 * Embedded entry point.
 *
 * The host application receives a single-use, origin-bound session and frames
 * this route. Redeeming it once yields a short-lived signing credential; from
 * there the signer sees exactly the hosted experience, because consent,
 * authentication and evidence controls must not be weakened by embedding.
 */
export function Embed() {
  const { token = '' } = useParams();
  const [state, setState] = useState<
    { status: 'loading' }
    | { status: 'error'; message: string; code?: string }
    | { status: 'ready'; signingToken: string; readOnly: boolean;
        sessionToken?: string }
    | { status: 'sender'; envelopeId: string; operation: string; detail: any }
    | { status: 'prepare'; sessionToken: string }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    api.post(`/embed/${token}/redeem`)
      .then((result) => {
        if (cancelled) return;
        if (result.sender_session) {
          setState({ status: 'prepare', sessionToken: result.sender_session });
        } else if (result.signing_token) {
          setState({ status: 'ready', signingToken: result.signing_token,
                     readOnly: Boolean(result.read_only),
                     sessionToken: result.session_token });
        } else {
          setState({ status: 'sender', envelopeId: result.envelope_id,
                     operation: result.operation, detail: result });
        }
      })
      .catch((cause) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: cause instanceof ApiError ? cause.message
                                             : 'This embedded session could not be opened.',
          code: cause instanceof ApiError ? cause.code : undefined,
        });
      });
    return () => { cancelled = true; };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="shell shell--narrow section">
        <Loading label="Opening your agreement…" />
      </div>
    );
  }

  if (state.status === 'error') {
    const expired = state.code === 'embedded_session_expired'
      || state.code === 'embedded_session_consumed';
    return (
      <div className="shell shell--narrow section">
        <Banner tone="danger" title="This embedded session cannot be opened">
          {state.message}
          {expired && (
            <p className="small" style={{ margin: '8px 0 0' }}>
              Embedded sessions are single use and expire after a few minutes.
              Ask the host application to start a new one.
            </p>
          )}
        </Banner>
      </div>
    );
  }

  if (state.status === 'prepare') {
    return <EmbeddedPrepare sessionToken={state.sessionToken} />;
  }

  if (state.status === 'sender') {
    const detail = state.detail;
    return (
      <div className="shell shell--narrow section stack">
        <div className="card stack-s">
          <div className="row row--between row--top">
            <h1 style={{ margin: 0, fontSize: '1.15rem' }}>{detail.title}</h1>
            <StatusPill status={detail.status} />
          </div>
          <p className="card__sub" style={{ margin: 0 }}>
            Sent {formatDateTime(detail.sent_at)}
            {detail.completed_at && ` · Completed ${formatDateTime(detail.completed_at)}`}
          </p>
        </div>
        <div className="card stack-s">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Recipients</h2>
          <div className="list">
            {(detail.recipients || []).map((person: any, index: number) => (
              <div className="panel row row--between" key={index}>
                <span className="truncate">
                  {person.name}
                  <span className="muted small">
                    {' '}· {person.role === 'copy' ? 'copy only'
                             : `step ${person.routing_order}`}
                  </span>
                </span>
                <StatusPill status={person.status} />
              </div>
            ))}
          </div>
        </div>
        <p className="small muted">
          This is a read-only tracking view. Preparing and sending agreements
          happens in the dashboard or through the API.
        </p>
      </div>
    );
  }

  // Deliberately the same component the hosted journey uses.
  return <SignExperience token={state.signingToken} embedded
                         readOnly={state.readOnly}
                         initialSession={state.sessionToken} />;
}
